import dayjs from 'dayjs';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { PropTypes } from 'prop-types';
import React, { useState, useCallback, useMemo, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { ThemeContext } from 'styled-components';

import { Button } from '@ge/components/button';
import { Icon, Icons } from '@ge/components/icon';
import { Loader } from '@ge/components/loader';
import { Dialog } from '@ge/components/modal';
import { Table, Tbody, Td, Th, Thead } from '@ge/components/table';
import { TooltipCell } from '@ge/components/table/table';
import { Text } from '@ge/components/typography';
import {
  PanelColumnWidths,
  EntityType,
  Capability,
  AlarmType,
  EntityTab,
  Feature,
  TaskSourceField,
  SeverityType,
  SeverityLevel,
  SeverityTooltip,
  AssetCommand,
} from '@ge/models/constants';
import { AuthRender } from '@ge/shared/components/auth-render';
import { NewTaskDialog } from '@ge/shared/components/tasks/new-task-dialog';
import { EntityDetailsContext, useAppNotificationContext } from '@ge/shared/context';
import { useAssetCommandsHistory } from '@ge/shared/data-hooks/use-asset-command-history';
import { sendCommand } from '@ge/shared/services/commands';
import { getDuration, getDateTimeBasedOnZone } from '@ge/shared/util/time-date';
import { elevations } from '@ge/tokens/elevations';
import { typography } from '@ge/tokens/typography';
import { recursiveSort } from '@ge/util/metric-sorter';
import { toLocaleKey } from '@ge/util/string-utils';

const ACK_RESET_COMMAND_NAME = AssetCommand.REM_ACK_RESET;
const ACK_RESET_NOTIFICATION_KEY = 'ack_reset_command';

const StyledTr = styled.tr`
  line-height: 16px;
  opacity: ${(props) => (props.isDisconnected ? 0.4 : 1)};
  background: ${(props) => {
    if (props.$severityRowBg) return `${props.$severityRowBg} !important`;
    if (props.$level > 0) return props.theme.colors.slate1;
    return 'transparent';
  }};

  &:hover,
  &:active,
  &[aria-selected='true'] {
    background: ${(props) =>
      props.$severityRowBg ? `${props.$severityRowBg} !important` : undefined};
  }
`;

const StyledTd = styled(Td)`
  padding: 4px 8px;
  font-size: 11px;
  max-width: 0;
  overflow: hidden;
`;

const AckResetTd = styled(Td)`
  padding: 0 2px;
  min-width: 0;
  overflow: visible;
`;

const StyledCode = styled.span`
  display: inline-block;
  font-size: 12px;
  vertical-align: middle;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const StyledDesc = styled.span`
  margin-left: 6px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 1;
`;

const StyledTimeDate = styled.div`
  span {
    display: inline-block;

    &:first-of-type {
      margin-right: 10px;
    }

    &:last-of-type {
      color: ${(props) => props.theme.table.textLightColor};
    }
  }
`;

const FHPIcon = styled(Icon).attrs((props) => ({
  size: 18,
  icon: Icons.CASE_PROCEDURE,
  color: props.theme.table.iconLightColor,
}))`
  padding-bottom: 2px;
`;

const ExpandIcon = styled(Icon).attrs((props) => ({
  icon: Icons.TRIANGLE_RIGHT_SOLID,
  size: 14,
  color: props.theme.colors?.text || (props.theme.name === 'Dark' ? '#EAEAEA' : '#555555'),
}))``;

const AckResetIcon = styled(Icon).attrs((props) => ({
  size: 18,
  icon: Icons.POINTER,
  color: props.theme.table.arrowColor,
}))``;

const FHPButton = styled.button`
  padding-top: 4px;
  height: 20px;
  width: 20px;
`;

const AckResetButton = styled.button.attrs(() => ({
  type: 'button',
}))`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  width: 20px;
  padding: 0;
`;

const AddIcon = styled(Icon).attrs((props) => ({
  size: 8.33,
  icon: Icons.ADD,
  color: props.theme.colors.slate5,
}))`
  background: ${(props) => props.theme.colors.slate2};
  border: 1px solid ${(props) => props.theme.colors.slate12};
  padding: 2px;

  &:active {
    border: 1px solid ${(props) => props.theme.themeSelector.iconColorActive};
  }
`;

const SeverityIcon = styled.svg`
  display: inline-block;
  padding-top: 2px;
  vertical-align: middle;
  height: 22px;
  width: 16px;
  & path:nth-child(1) {
    fill: ${(props) => props.theme.eventSeverity.safetyCritical};
  }
  & path:nth-child(2),
  path:nth-child(3) {
    fill: ${(props) => props.theme.eventSeverity.safetyCriticalExclamatory};
  }
`;

const AlarmIcon = styled(Icon).attrs(() => ({
  size: 18,
  icon: Icons.FAULT_ALARM,
  viewbox: '0 0 16 16',
}))`
  padding-top: 2px;
  path {
    fill: none;
    stroke: ${(props) => props.theme.eventSeverity.falutAlarm};
  }
`;

const WarningIcon = styled(Icon).attrs(() => ({
  size: 18,
  icon: Icons.WARNING_SEVERITY,
  viewbox: '0 0 16 16',
}))`
  padding-top: 2px;
  path {
    fill: none;
    stroke: ${(props) => props.theme.eventSeverity.warning};
  }
`;

const InformationIcon = styled(Icon).attrs(() => ({
  size: 18,
  icon: Icons.INFORMATIONAL_SEVERITY,
  viewbox: '0 0 16 16',
}))`
  padding-top: 2px;
  path {
    fill: none;
    stroke: ${(props) => props.theme.eventSeverity.informational};
  }
`;

const SeverityLevelIcon = styled.svg`
  display: inline-block;
  padding-top: 2px;
  vertical-align: middle;
  height: 22px;
  width: 16px;
`;

const TreeCellContainer = styled.div`
  display: flex;
  align-items: center;
  padding-left: ${(props) => props.$level * 18}px;
  overflow: hidden;
  min-width: 0;
  flex: 1;
`;

const ExpanderButton = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-right: 2px;
  transform: ${(props) => (props.expanded ? 'rotate(90deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease;
  visibility: ${(props) => (props.visible ? 'visible' : 'hidden')};
  opacity: ${(props) => (props.visible ? 1 : 0)};
`;

const AckResetDialogContainer = styled.div`
  width: 460px;
  max-height: calc(80vh - 80px);
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 10px;
    border-radius: 1px;
    background-color: rgba(12, 15, 18, 0.24);
  }

  &::-webkit-scrollbar-thumb {
    height: 100px;
    border: 2px solid transparent;
    background-clip: content-box;
    border-radius: 2.5px;
    background-color: ${(props) => props.theme.table.fixedBorderColor};
  }
`;

const AckResetText = styled(Text).attrs(() => ({
  type: typography.textTypes.body,
  level: 1,
}))`
  color: ${(props) => props.theme.layout.textColor};
`;

const AckResetDialogMessage = styled.div`
  margin-bottom: 12px;
  line-height: 20px;
`;

const AckResetDialogHeader = styled.div`
  display: flex;
  margin-bottom: 16px;
  .spacer {
    margin: 0 8px;
    font-weight: bold;
  }
`;

const AckResetDialogItem = styled.div`
  display: flex;
  align-items: center;
`;

const AckResetDetailsTable = styled.table`
  width: 100%;
  margin-top: 6px;
  border-collapse: collapse;
  background: ${(props) => props.theme.eventController.commandDialog.tableBackground};
`;

const AckResetDetailsHeaderCell = styled(Th).attrs(() => ({
  align: 'left',
}))`
  cursor: pointer;
`;

const AckResetDetailsDataRow = styled.tr`
  background: transparent !important;

  &:hover,
  &:active,
  &[aria-selected='true'] {
    background: transparent !important;
  }
`;

const AckResetDetailsDataCell = styled.td`
  padding: 8px 12px 2px;
  text-align: left;
  word-break: break-word;
  background: transparent !important;
  border-color: transparent !important;
  border-bottom: 0 !important;
  box-shadow: none !important;
  outline: none !important;

  &:hover,
  &:active,
  &[aria-selected='true'] {
    background: transparent !important;
    border-color: transparent !important;
    box-shadow: none !important;
    outline: none !important;
  }
`;

const AckResetRequestsContainer = styled.div`
  display: flex;
  flex: 1;
  height: 120px;
  margin: 6px;
  overflow: hidden;
`;

const AckResetRequestsTable = styled(Table).attrs((props) => ({
  compressed: true,
  transparent: true,
  backgroundColor: props.theme.eventController.commandDialog.tableBackground,
}))``;

const AckResetRequestCell = styled(Td)`
  padding: 2px 12px;
  background: ${(props) => props.theme.eventController.commandDialog.tdBackground};
`;

const AckResetDialogFooter = styled.div`
  margin-left: auto;
  text-align: right;
  button:not(:last-of-type) {
    margin-right: 5px;
  }
`;

const AckResetButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const AckResetDclrSegment = styled.div`
  margin-top: 6px;
  margin-bottom: 6px;
  background-color: #2a323b;
  padding: 10px 16px;
  color: #999999;
`;

const AckResetDclrHeader = styled.div`
  font-weight: bold;
  margin-bottom: 5px;
  margin-left: 10px;
  color: ${(props) => props.theme.layout.textColor};
`;

const AckResetDclrText = styled.div`
  font-size: 12px;
  font-weight: 200;
  line-height: 20px;
`;

const AssetAlarmsTable = ({
  alarms,
  sortAction,
  sortMetric,
  sortDirection,
  isLoading,
  isDisconnected,
  scrollable,
  assetId,
  siteId,
}) => {
  const { t, ready } = useTranslation(['entity-details'], {
    useSuspense: false,
  });
  const theme = useContext(ThemeContext);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const { showAssetDetails, showSiteDetails } = useContext(EntityDetailsContext);
  const [selectedAckResetAlarm, setSelectedAckResetAlarm] = useState(null);
  const [isAckResetSubmitting, setIsAckResetSubmitting] = useState(false);

  const { getSiteById } = useStoreState((store) => store.sites);
  const notifications = useAppNotificationContext();

  const site = getSiteById(siteId);
  const timezone = site?.timezone;

  const { getAssetById } = useStoreState((store) => store.assets);
  const asset = getAssetById(assetId);

  const {
    data: { commandsHistory: ackResetCommandHistory = [] } = {},
    refetch: refetchAckResetHistory,
  } = useAssetCommandsHistory(asset, Boolean(selectedAckResetAlarm));
  const { notify } = notifications[Feature.MONITOR]?.commands ?? {};

  const { setSelectedAlarmOrEvent } = useStoreActions((actions) => actions.tasks);
  const { setWorkStandards } = useStoreActions((actions) => actions.tasks);

  const displayAlarms = useMemo(() => {
    const rawData = alarms && alarms.length > 0 ? alarms : [];

    let sorted;
    if (sortMetric === 'eventClass') {
      const dir = sortDirection === 'asc' ? 1 : -1;
      const severitySort = (items) => {
        if (!items || !items.length) return [];
        return [...items]
          .sort(
            (a, b) =>
              dir *
              ((SeverityLevel[a?.eventClass] ?? Infinity) -
                (SeverityLevel[b?.eventClass] ?? Infinity)),
          )
          .map((item) =>
            item.children && item.children.length > 0
              ? { ...item, children: severitySort(item.children) }
              : item,
          );
      };
      sorted = severitySort(rawData);
    } else {
      sorted = sortMetric ? recursiveSort(rawData, sortMetric, sortDirection) : rawData;
    }

    const sortByStartDesc = (a, b) => {
      const aStart = a?.start ? new Date(a.start).getTime() : 0;
      const bStart = b?.start ? new Date(b.start).getTime() : 0;
      return bStart - aStart;
    };

    const collectByLevel = (items, targetLevel) => {
      const result = [];
      const walk = (nodes) => {
        if (!nodes) return;
        for (const node of nodes) {
          if ((SeverityLevel[node?.eventClass] ?? Infinity) === targetLevel) {
            result.push(node);
          }
          if (node.children && node.children.length > 0) {
            walk(node.children);
          }
        }
      };
      walk(items);
      return result;
    };

    const level1 = collectByLevel(rawData, 1).sort(sortByStartDesc);
    const level2 = collectByLevel(rawData, 2).sort(sortByStartDesc);
    const rest = sorted.filter(
      (alarm) =>
        (SeverityLevel[alarm?.eventClass] ?? Infinity) !== 1 &&
        (SeverityLevel[alarm?.eventClass] ?? Infinity) !== 2,
    );

    return { level1, level2, rest };
  }, [alarms, sortMetric, sortDirection]);

  const sortedDirection = useCallback(
    (metric) => (metric === sortMetric ? sortDirection : ''),
    [sortMetric, sortDirection],
  );

  const getIconType = useCallback((severity) => {
    switch (severity) {
      case SeverityType.LEVEL_1:
        return (
          <SeverityIcon viewBox="0 0 16 12">
            <path d={Icons.SAFETY_POLYGON} />
            <path d={Icons.SAFETY_EXCLAMATORY} />
            <path d={Icons.SAFETY_CIRCLE} />
          </SeverityIcon>
        );
      case SeverityType.LEVEL_2:
        return (
          <SeverityLevelIcon viewBox="0 0 16 16">
            <path d={Icons.SEVERITY_LEVEL2_BG} fill="#FD7E26" stroke="white" strokeWidth="0.5" />
            <path d={Icons.SEVERITY_LEVEL2_TEXT} fill="black" />
          </SeverityLevelIcon>
        );
      case SeverityType.LEVEL_3:
        return (
          <SeverityLevelIcon viewBox="0 0 16 16">
            <path d={Icons.SEVERITY_LEVEL3_BG} fill="#FD7E26" stroke="white" strokeWidth="0.5" />
            <path d={Icons.SEVERITY_LEVEL3_TEXT} fill="black" />
          </SeverityLevelIcon>
        );
      case SeverityType.LEVEL_4:
        return (
          <SeverityLevelIcon viewBox="0 0 17 16">
            <path d={Icons.SEVERITY_LEVEL4_BG} fill="#FFC90D" stroke="white" strokeWidth="0.5" />
            <path d={Icons.SEVERITY_LEVEL4_TEXT} fill="black" />
          </SeverityLevelIcon>
        );
      case SeverityType.LEVEL_5:
        return (
          <SeverityLevelIcon viewBox="0 0 17 16">
            <path d={Icons.SEVERITY_LEVEL5_BG} fill="#A349A4" stroke="white" strokeWidth="0.5" />
            <path d={Icons.SEVERITY_LEVEL5_TEXT} fill="white" />
          </SeverityLevelIcon>
        );
      case SeverityType.LEVEL_6:
        return (
          <SeverityLevelIcon viewBox="0 0 17 16">
            <path d={Icons.SEVERITY_LEVEL6_BG} fill="#02A2E7" stroke="white" strokeWidth="0.5" />
            <path d={Icons.SEVERITY_LEVEL6_EXTRA} fill="white" />
          </SeverityLevelIcon>
        );
      case SeverityType.LEVEL_7:
        return (
          <SeverityLevelIcon viewBox="0 0 16 16">
            <path d={Icons.SEVERITY_LEVEL7_BG} fill="#22B14C" stroke="white" strokeWidth="0.5" />
            <path d={Icons.SEVERITY_LEVEL7_TEXT} fill="white" />
          </SeverityLevelIcon>
        );
      // Legacy severity types (backward compatible)
      case SeverityType.ALARM:
        return <AlarmIcon />;
      case SeverityType.WARNING:
        return <WarningIcon />;
      case SeverityType.INFORMATION:
        return <InformationIcon />;
      case SeverityType.CRITICAL:
        return (
          <SeverityIcon viewBox="0 0 16 12">
            <path d={Icons.SAFETY_POLYGON} />
            <path d={Icons.SAFETY_EXCLAMATORY} />
            <path d={Icons.SAFETY_CIRCLE} />
          </SeverityIcon>
        );
      default:
        return '';
    }
  }, []);

  const spawnWindow = useCallback((type, fhpId, fhpCode) => {
    window.open(
      `/${type}/${fhpId}/${fhpCode}`,
      '_blank',
      'toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=780px,height=800px',
    );
  }, []);

  const newFieldTask = useCallback(
    (alarm, assetId) => {
      setShowNewTaskModal(true);
      alarm.assetId = assetId;
      setSelectedAlarmOrEvent(alarm);
    },
    [setShowNewTaskModal, setSelectedAlarmOrEvent],
  );

  const closeModal = useCallback(() => {
    setShowNewTaskModal(false);
    setSelectedAlarmOrEvent(null);
    setWorkStandards(null);
  }, [setSelectedAlarmOrEvent, setWorkStandards]);

  const saveTask = useCallback(() => {
    setShowNewTaskModal(false);
    setSelectedAlarmOrEvent(null);
    setWorkStandards(null);
  }, [setWorkStandards, setSelectedAlarmOrEvent]);

  const toggleRow = (e, toggleKey) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setExpandedRows((prev) => ({
      ...prev,
      [toggleKey]: !prev[toggleKey],
    }));
  };

  const openAckResetDialog = useCallback((e, alarm) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setIsAckResetSubmitting(false);
    setSelectedAckResetAlarm(alarm);
  }, []);

  const closeAckResetDialog = useCallback(() => {
    setIsAckResetSubmitting(false);
    setSelectedAckResetAlarm(null);
  }, []);

  const executeAckResetCommand = useCallback(async () => {
    if (!asset?.id || !asset?.site?.id || !selectedAckResetAlarm?.code || isAckResetSubmitting) {
      return;
    }

    setIsAckResetSubmitting(true);

    try {
      const notificationAsset = {
        ...asset,
        site: {
          ...asset.site,
          id: asset.site?.id || site?.id,
          name: asset.site?.name || site?.name,
        },
      };

      const response = await sendCommand({
        assets: [
          {
            cmdNo: 1,
            siteId: asset.site.id,
            turbineId: asset.id,
          },
        ],
        command: ACK_RESET_COMMAND_NAME,
        value: String(selectedAckResetAlarm.code),
      });

      if (response?.statusId && notify) {
        notify({
          assets: [notificationAsset],
          command: ACK_RESET_NOTIFICATION_KEY,
          statusId: response.statusId,
          onAssetDetails: (id) => showAssetDetails(id, EntityTab.REALTIME_INFO),
          onSiteDetails: (id) => showSiteDetails(id, EntityTab.REALTIME_INFO),
        });
      }

      closeAckResetDialog();
    } catch (error) {
      console.error('Failed to execute RemAckReset command', error);
    } finally {
      setIsAckResetSubmitting(false);
    }
  }, [
    asset,
    closeAckResetDialog,
    isAckResetSubmitting,
    notify,
    selectedAckResetAlarm?.code,
    site?.id,
    site?.name,
    showAssetDetails,
    showSiteDetails,
  ]);

  useEffect(() => {
    if (selectedAckResetAlarm) {
      refetchAckResetHistory();
    }
  }, [refetchAckResetHistory, selectedAckResetAlarm]);

  const headerTimezoneAbbr = (() => {
    try {
      return dayjs.tz(new Date(), timezone).format('z');
    } catch {
      return '';
    }
  })();

  const ackResetHistoryRows = useMemo(
    () =>
      ackResetCommandHistory.filter(
        (item) =>
          item.type === '' ||
          item.type === ACK_RESET_COMMAND_NAME ||
          item.cmdName === ACK_RESET_COMMAND_NAME,
      ),
    [ackResetCommandHistory],
  );

  const getAckResetHistoryType = useCallback(
    (item) => {
      if (
        item?.cmdName === ACK_RESET_COMMAND_NAME ||
        item?.type === ACK_RESET_COMMAND_NAME ||
        item?.type === ''
      ) {
        return item?.value
          ? `${ACK_RESET_COMMAND_NAME.toUpperCase()} - ${item.value}`
          : ACK_RESET_COMMAND_NAME.toUpperCase();
      }

      return t(`commands.${toLocaleKey(item?.type)}`, item?.type || '--');
    },
    [t],
  );

  if (!ready) {
    return <Loader />;
  }

  const currentTime = new Date().getTime();

  const renderRow = (alarm, index, level = 0, parentKey = 'root', severityRowBg = null) => {
    if (!alarm) return null;
    if (level > 5) return null;

    const uniqueRowKey = `${parentKey}-${alarm.eventId}-${index}`;
    const hasChildren = alarm.children && alarm.children.length > 0;
    const toggleKey = alarm.eventId || `${parentKey}-${index}`;
    const isExpanded = !!expandedRows[toggleKey];

    let alarmDateTime = null;
    try {
      if (alarm.start && alarm.start !== 'Invalid Date') {
        alarmDateTime = getDateTimeBasedOnZone(alarm.start, timezone);
      }
    } catch (e) {
      console.warn('Date parse error', e);
    }

    return (
      <React.Fragment key={uniqueRowKey}>
        <StyledTr isDisconnected={isDisconnected} $level={level} $severityRowBg={severityRowBg}>
          <StyledTd border align="left" width={PanelColumnWidths.XSMALL}>
            <TooltipCell
              tooltip={SeverityTooltip[alarm.eventClass] || alarm.eventClass}
              zIndex={elevations.P20}
            >
              {alarm?.eventClass ? getIconType(alarm?.eventClass) : ''}
            </TooltipCell>
          </StyledTd>

          <StyledTd border align="left" width={PanelColumnWidths.MEDIUM}>
            <TooltipCell tooltip={alarm.code} zIndex={elevations.P20}>
              <StyledCode>{alarm.code}</StyledCode>
            </TooltipCell>
          </StyledTd>

          <StyledTd border align="left" width={'300px'}>
            <TreeCellContainer $level={level}>
              <ExpanderButton
                visible={hasChildren}
                expanded={isExpanded}
                onClick={(e) => toggleRow(e, toggleKey)}
              >
                <ExpandIcon />
              </ExpanderButton>

              <StyledDesc title={alarm.description}>{alarm.description}</StyledDesc>
            </TreeCellContainer>
          </StyledTd>

          <StyledTd border align="left" width={PanelColumnWidths.MEDIUM}>
            {getDuration(alarm.start, dayjs(currentTime)).formatted}
          </StyledTd>

          <StyledTd border align="left" width={PanelColumnWidths.LARGE}>
            {alarmDateTime && (
              <StyledTimeDate>
                <span>{alarmDateTime.timeInSecs}</span>
                <span>{alarmDateTime.date}</span>
              </StyledTimeDate>
            )}
          </StyledTd>

          <AuthRender
            capability={Capability.FAULT_HANDLING}
            view
            description="Handling procedure"
            siteIds={[siteId]}
          >
            <StyledTd border align="center" width={PanelColumnWidths.XSMALL}>
              {alarm.category === AlarmType.NON_DIAG && (
                <FHPButton
                  onClick={() => spawnWindow(EntityType.HANDLING_PROCEDURE, assetId, alarm.eventId)}
                >
                  <FHPIcon />
                </FHPButton>
              )}
            </StyledTd>
          </AuthRender>

          <AckResetTd border align="center" width={'20px'}>
            {alarm.category === AlarmType.NON_DIAG &&
              alarm.commandProperties?.blockAckReset === true && (
                <AckResetButton onClick={(e) => openAckResetDialog(e, alarm)}>
                  <AckResetIcon />
                </AckResetButton>
              )}
          </AckResetTd>

          <AuthRender
            capability={Capability.FIELD_TASKS}
            create
            description="New Task button"
            siteLevel={false}
          >
            <StyledTd border align="center" width={PanelColumnWidths.XXSMALL}>
              <button onClick={() => newFieldTask(alarm, assetId)}>
                <AddIcon />
              </button>
            </StyledTd>
          </AuthRender>
        </StyledTr>

        {hasChildren &&
          isExpanded &&
          alarm.children.map((child, childIdx) =>
            renderRow(child, childIdx, level + 1, uniqueRowKey),
          )}
      </React.Fragment>
    );
  };

  return (
    <>
      <Table height={400} compressed isLoading={isLoading} scrollable={scrollable}>
        <Thead transparent noTitles>
          <StyledTr>
            <Th
              labels={{
                [t('alarms_table.sev', 'Sev.')]: 'eventClass',
              }}
              align="left"
              sortedDirection={sortedDirection}
              onClick={sortAction}
              width={PanelColumnWidths.XXSMALL}
            />
            <Th
              labels={{
                [t('alarms_table.code', 'Code / #')]: 'code',
              }}
              align="left"
              sortedDirection={sortedDirection}
              onClick={sortAction}
              width={PanelColumnWidths.MEDIUM}
            />
            <Th
              labels={{
                [t('alarms_table.title', 'Title')]: 'description',
              }}
              align="left"
              sortedDirection={sortedDirection}
              onClick={sortAction}
              width={'300px'}
            />
            <Th
              labels={{
                [t('alarms_table.duration', 'Duration')]: 'duration',
              }}
              align="left"
              sortedDirection={sortedDirection}
              onClick={sortAction}
              width={PanelColumnWidths.SMALL}
            />
            <Th
              labels={{
                [`${t('alarms_table.start', 'Start')} (${headerTimezoneAbbr})`]: 'start',
              }}
              align="left"
              sortedDirection={sortedDirection}
              onClick={sortAction}
              width={PanelColumnWidths.LARGE}
            />
            <AuthRender
              capability={Capability.FAULT_HANDLING}
              view
              description="Handling procedure"
              siteIds={[siteId]}
            >
              <Th
                labels={{
                  [t('alarms_table.handling_procedure', 'Handling\nProcedure')]: 'category',
                }}
                align="center"
                sortedDirection={sortedDirection}
                onClick={sortAction}
                width={PanelColumnWidths.XSMALL}
              />
            </AuthRender>
            <AuthRender
              capability={Capability.ROC_COMMANDS}
              create
              description="ROC Commands"
              siteLevel={false}
            >
              <Th
                labels={{
                  [t('alarms_table.ack_reset_command', 'Remote Ack\nReset Command')]: 'ack reset command',
                }}
                align="center"
                sortedDirection={sortedDirection}
                onClick={sortAction}
                width={'72px'}
              />
            </AuthRender>
            <AuthRender
              capability={Capability.FIELD_TASKS}
              create
              description="New Task button"
              siteLevel={false}
            >
              <Th
                labels={{
                  [t('alarms_table.task_escalation', 'Task\nEscalation')]: 'task escalation',
                }}
                align="center"
                width={PanelColumnWidths.XXSMALL}
              />
            </AuthRender>
          </StyledTr>
        </Thead>
        <Tbody transparent>
          {displayAlarms.level1.map((alarm, idx) =>
            renderRow(alarm, idx, 0, 'pinned-l1', theme.eventSeverity.level1RowBg),
          )}
          {displayAlarms.level2.map((alarm, idx) => renderRow(alarm, idx, 0, 'pinned-l2'))}
          {displayAlarms.rest.map((alarm, idx) => renderRow(alarm, idx, 0, 'root'))}

          {showNewTaskModal && (
            <NewTaskDialog
              entity={asset}
              onClose={closeModal}
              onConfirm={saveTask}
              taskSource={TaskSourceField.REALTIMECASES}
              isCalledfromTaskEscalationColumn={true}
            />
          )}
        </Tbody>
      </Table>
      {selectedAckResetAlarm && asset && (
        <Dialog
          isOpen={true}
          onClose={closeAckResetDialog}
          header={'Remote Acknowledge Reset Command Confirmation'}
          footer={
            <AckResetDialogFooter>
              <Button onClick={closeAckResetDialog}>{t('general:cancel', 'Cancel')}</Button>
              <Button
                primary
                onClick={executeAckResetCommand}
                disabled={isAckResetSubmitting || !selectedAckResetAlarm?.code}
              >
                <AckResetButtonContent>{t('general:execute', 'Execute')}</AckResetButtonContent>
              </Button>
            </AckResetDialogFooter>
          }
          contentWidth
        >
          <AckResetDialogContainer>
            <AckResetDialogMessage>
              <AckResetText>
                {t('general:commands.attempting_msg', 'You are attempting to')}:{' '}
                <b>Remote Acknowledge Reset Command</b>
              </AckResetText>
            </AckResetDialogMessage>
            <AckResetDialogHeader>
              <AckResetDialogItem>
                <Icon icon={Icons.SITE} size={14} color={theme.entityDetails.headerIconColor} />
                <AckResetText>
                  <b>{site?.name || '--'}</b>
                </AckResetText>
              </AckResetDialogItem>
              <AckResetText className="spacer">/</AckResetText>
              <AckResetDialogItem>
                <Icon icon={Icons.TURBINE} size={14} color={theme.entityDetails.headerIconColor} />
                <AckResetText>
                  <b>{asset?.name || '--'}</b>
                </AckResetText>
              </AckResetDialogItem>
            </AckResetDialogHeader>
            <AckResetDetailsTable>
              <Thead transparent compressed>
                <tr>
                  <AckResetDetailsHeaderCell>Event Code</AckResetDetailsHeaderCell>
                  <AckResetDetailsHeaderCell>Title</AckResetDetailsHeaderCell>
                </tr>
              </Thead>
              <tbody>
                <AckResetDetailsDataRow>
                  <AckResetDetailsDataCell>
                    <AckResetText>{selectedAckResetAlarm.code || '--'}</AckResetText>
                  </AckResetDetailsDataCell>
                  <AckResetDetailsDataCell>
                    <AckResetText>{selectedAckResetAlarm.description || '--'}</AckResetText>
                  </AckResetDetailsDataCell>
                </AckResetDetailsDataRow>
              </tbody>
            </AckResetDetailsTable>
            <AckResetDclrSegment>
              <AckResetDclrText>{'Disclaimer'}</AckResetDclrText>
              <AckResetDclrText>
                {`Confirm the required actions for notification ${selectedAckResetAlarm?.code || '--'} are complete.`}
              </AckResetDclrText>
            </AckResetDclrSegment>
            {ackResetHistoryRows.length > 0 && (
              <AckResetRequestsContainer>
                <AckResetRequestsTable scrollable>
                  <Thead transparent compressed>
                    <tr>
                      <Th
                        labels={{
                          [t('commands_dialog.command_type', 'Command Type')]: 'command_type',
                        }}
                        align="left"
                      />
                      <Th
                        labels={{ [t('commands_dialog.status', 'Status')]: 'status' }}
                        align="left"
                      />
                      <Th
                        labels={{
                          [t('commands_dialog.issued_on', 'Issued on') +
                          ` (${headerTimezoneAbbr})`]: 'issued_on',
                        }}
                        align="left"
                      />
                      <Th
                        labels={{ [t('commands_dialog.issued_by', 'Issued By')]: 'issued_by' }}
                        align="left"
                      />
                    </tr>
                  </Thead>
                  <Tbody transparent compressed>
                    {ackResetHistoryRows.map((item, idx) => {
                      let itemDateTime = null;

                      try {
                        itemDateTime = item?.date
                          ? getDateTimeBasedOnZone(item.date, timezone)
                          : null;
                      } catch {
                        itemDateTime = null;
                      }

                      return (
                        <tr key={`${item?.type || 'ack-reset'}-${item?.date || idx}`}>
                          <AckResetRequestCell align="left">
                            {getAckResetHistoryType(item)}
                          </AckResetRequestCell>
                          <AckResetRequestCell align="left">
                            {item?.status ? t(`commands_dialog.${item.status}`, item.status) : '--'}
                          </AckResetRequestCell>
                          <AckResetRequestCell align="left">
                            {itemDateTime
                              ? `${itemDateTime.timeInSecs}, ${itemDateTime.date}`
                              : '--'}
                          </AckResetRequestCell>
                          <AckResetRequestCell align="left">
                            <TooltipCell tooltip={item?.username || '--'} zIndex={elevations.P100}>
                              {item?.username || '--'}
                            </TooltipCell>
                          </AckResetRequestCell>
                        </tr>
                      );
                    })}
                  </Tbody>
                </AckResetRequestsTable>
              </AckResetRequestsContainer>
            )}
          </AckResetDialogContainer>
        </Dialog>
      )}
    </>
  );
};

AssetAlarmsTable.propTypes = {
  alarms: PropTypes.arrayOf(PropTypes.instanceOf(Object)).isRequired,
  sortAction: PropTypes.func,
  sortMetric: PropTypes.string,
  sortDirection: PropTypes.string,
  onEventSelect: PropTypes.func,
  isLoading: PropTypes.bool,
  scrollable: PropTypes.bool,
  isDisconnected: PropTypes.bool,
  assetId: PropTypes.string,
  siteId: PropTypes.string.isRequired,
};

AssetAlarmsTable.defaultProps = {
  sortAction: () => null,
  sortMetric: '',
  sortDirection: '',
  onEventSelect: () => null,
  isLoading: false,
  scrollable: false,
  isDisconnected: false,
};

export default AssetAlarmsTable;
